import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  RotateCcw, AlertTriangle, CheckCircle2, Ban, FileText, ShieldAlert, Info, User2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  committed: { label: "Importação ativa", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  partially_reverted: { label: "Rollback parcial", cls: "bg-amber-100 text-amber-900 border-amber-200" },
  reverted: { label: "Revertida", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.committed;
  return <Badge className={`${s.cls} border`}>{s.label}</Badge>;
}

export default function SpecificImportHistoryPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogSnap, setDialogSnap] = useState(null);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [executing, setExecuting] = useState(false);
  const [confirmExec, setConfirmExec] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees/specific-import/history?limit=100");
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao carregar histórico");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPreview = async (snap) => {
    setDialogSnap(snap);
    setPreview(null);
    setDecisions({});
    setResult(null);
    try {
      const { data } = await api.get(`/employees/specific-import/snapshots/${snap.id}/revert-preview`);
      setPreview(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao carregar preview");
      setDialogSnap(null);
    }
  };

  const toggleConflict = (key) => {
    setDecisions((prev) => ({ ...prev, [key]: prev[key] === "restore" ? undefined : "restore" }));
  };

  const forcedCount = useMemo(() =>
    Object.values(decisions).filter((v) => v === "restore").length, [decisions]);

  const doExecute = async () => {
    if (!dialogSnap) return;
    setExecuting(true);
    try {
      const { data } = await api.post(`/employees/specific-import/snapshots/${dialogSnap.id}/revert`, {
        force_conflicts: false,
        field_decisions: decisions,
      });
      setResult(data);
      setConfirmExec(false);
      toast.success(`Rollback aplicado — ${data.restored} campo(s) restaurado(s)`);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao executar rollback");
    } finally { setExecuting(false); }
  };

  return (
    <div className="space-y-4" data-testid="specific-history-panel">
      <div className="rounded-md border border-[#E8D9AE] bg-[#F9F4E7] px-3 py-2 text-[12px] text-[#5C4320] flex items-start gap-2">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#8A6B2B]" />
        <span>Cada importação específica gera um <b>snapshot reversível por campo</b>. Ao desfazer, o sistema
          detecta <b>conflitos por campo</b> — se um valor foi alterado depois da importação (manualmente ou por outra importação),
          preservamos o valor mais recente automaticamente. Você decide caso a caso se quer forçar a restauração.</span>
      </div>

      <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="specific-history-table">
        <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
          Histórico ({total})
        </div>
        {loading ? (
          <div className="p-4 text-muted-foreground text-[13px]">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Nenhuma importação específica registrada ainda.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Data / hora</TableHead>
                <TableHead>Arquivo</TableHead>
                <TableHead className="w-[220px]">Responsável</TableHead>
                <TableHead className="w-[100px] text-right">Colab.</TableHead>
                <TableHead className="w-[100px] text-right">Campos</TableHead>
                <TableHead className="w-[160px]">Status</TableHead>
                <TableHead className="w-[140px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`specific-history-row-${s.id}`}>
                  <TableCell className="text-[12px] font-mono">{fmtDate(s.imported_at)}</TableCell>
                  <TableCell className="text-[13px]">{s.filename || "—"}</TableCell>
                  <TableCell className="text-[12.5px]">{s.imported_by_name || s.imported_by_email}</TableCell>
                  <TableCell className="text-right font-mono">{s.atualizados}</TableCell>
                  <TableCell className="text-right font-mono">{s.total_campos}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={s.status === "reverted"}
                      onClick={() => openPreview(s)}
                      data-testid={`specific-history-revert-btn-${s.id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" />
                      {s.status === "reverted" ? "Já revertida" : "Analisar rollback"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!dialogSnap} onOpenChange={() => { setDialogSnap(null); setResult(null); }}>
        <DialogContent className="max-w-5xl" data-testid="specific-revert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-[#234935]" /> Desfazer importação · {dialogSnap?.filename}
            </DialogTitle>
            <DialogDescription>
              Importada por {dialogSnap?.imported_by_name} em {fmtDate(dialogSnap?.imported_at)} ·{" "}
              {dialogSnap?.atualizados} colaboradores · {dialogSnap?.total_campos} campos
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="specific-revert-result">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                  <div className="font-display font-semibold text-emerald-900">Rollback concluído</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[13px]">
                  <SumCard label="Restaurados" value={result.restored} tone="emerald" />
                  <SumCard label="Conflitos preservados" value={result.kept_conflicts} tone="amber" />
                  <SumCard label="Já revertidos" value={result.already_reverted} tone="slate" />
                  <SumCard label="Sem colaborador" value={result.missing_employee} tone="rose" />
                </div>
                <div className="mt-2 text-[12px] text-emerald-900">Status final: <b>{result.status}</b></div>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => { setDialogSnap(null); setResult(null); }}>Fechar</Button>
              </div>
            </div>
          ) : !preview ? (
            <div className="p-6 text-muted-foreground text-[13px]">Analisando alterações...</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <SumCard label="Pode restaurar" value={preview.counters.restore} tone="emerald" testId="revert-count-restore" />
                <SumCard label="Conflito por campo" value={preview.counters.conflict} tone="amber" testId="revert-count-conflict" />
                <SumCard label="Já no estado anterior" value={preview.counters.already_reverted} tone="slate" testId="revert-count-already" />
                <SumCard label="Sem colaborador" value={preview.counters.missing_employee} tone="rose" testId="revert-count-missing" />
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>Campos em <b>conflito</b> foram alterados depois da importação. Por padrão são
                  <b> preservados</b>. Marque a caixa para <b>forçar a restauração</b> de conflitos específicos.</span>
              </div>

              <div className="max-h-[52vh] overflow-y-auto rounded-md border border-slate-200 mt-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Matrícula</TableHead>
                      <TableHead>Colaborador</TableHead>
                      <TableHead>Campo</TableHead>
                      <TableHead>Valor anterior</TableHead>
                      <TableHead>Valor importado</TableHead>
                      <TableHead>Valor atual</TableHead>
                      <TableHead className="w-[130px]">Ação prevista</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.items.map((it) => {
                      const key = `${it.employee_id}::${it.campo}`;
                      const forcedRestore = decisions[key] === "restore";
                      const action = it.status === "restore" ? "restore"
                        : it.status === "already_reverted" ? "skip"
                        : it.status === "missing_employee" ? "skip"
                        : forcedRestore ? "restore" : "keep";
                      return (
                        <TableRow key={key} className={
                          it.status === "conflict" ? "bg-amber-50/40" :
                          it.status === "restore" ? "bg-emerald-50/30" : ""
                        } data-testid={`revert-row-${key}`}>
                          <TableCell className="font-mono text-[12px]">{it.matricula}</TableCell>
                          <TableCell className="text-[12.5px] truncate max-w-[140px]" title={it.nome}>{it.nome}</TableCell>
                          <TableCell className="text-[12.5px]">{it.label}</TableCell>
                          <TableCell className="font-mono text-[11.5px] text-muted-foreground">{it.valor_anterior || <em>(vazio)</em>}</TableCell>
                          <TableCell className="font-mono text-[11.5px]">{it.valor_importado || <em>(vazio)</em>}</TableCell>
                          <TableCell className="font-mono text-[11.5px] font-semibold">{it.valor_atual || <em>(vazio)</em>}</TableCell>
                          <TableCell>
                            {it.status === "restore" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">Restaurar</Badge>}
                            {it.status === "already_reverted" && <Badge variant="outline">Já revertido</Badge>}
                            {it.status === "missing_employee" && <Badge className="bg-rose-100 text-rose-800 border-rose-200 border">Sem colab</Badge>}
                            {it.status === "conflict" && (
                              <label className="flex items-center gap-1 cursor-pointer text-[12px]">
                                <input
                                  type="checkbox"
                                  checked={forcedRestore}
                                  onChange={() => toggleConflict(key)}
                                  data-testid={`revert-force-${key}`}
                                />
                                {forcedRestore
                                  ? <Badge className="bg-amber-100 text-amber-900 border-amber-200 border">Forçar restaurar</Badge>
                                  : <Badge variant="outline" className="text-muted-foreground">Preservar atual</Badge>}
                              </label>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <DialogFooter className="mt-3 gap-2">
                <Button variant="outline" onClick={() => setDialogSnap(null)}>Cancelar</Button>
                <Button
                  className="bg-[#234935] hover:bg-[#2c5c43]"
                  onClick={() => setConfirmExec(true)}
                  disabled={executing || (preview.counters.restore + forcedCount) === 0}
                  data-testid="specific-revert-execute-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Executar rollback ({preview.counters.restore + forcedCount} campos)
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmExec} onOpenChange={setConfirmExec}>
        <DialogContent data-testid="specific-revert-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-700" /> Confirmar rollback?</DialogTitle>
            <DialogDescription>
              {preview && <>Serão restaurados <b>{preview.counters.restore + forcedCount}</b> campo(s).{" "}
              {forcedCount > 0 && <><b>{forcedCount}</b> conflito(s) serão sobrescritos por decisão explícita.{" "}</>}
              Ação registrada em auditoria. Pode ser executada novamente depois.</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmExec(false)}>Voltar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={doExecute} disabled={executing} data-testid="specific-revert-confirm-btn">
              {executing ? "Executando..." : "Sim, executar rollback"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SumCard({ label, value, tone, testId }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-100",
    amber:   "bg-amber-50 text-amber-900 border-amber-100",
    slate:   "bg-slate-50 text-slate-900 border-slate-100",
    rose:    "bg-rose-50 text-rose-900 border-rose-100",
  }[tone];
  return (
    <div className={`rounded-md border p-2 ${tones}`} data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-wider opacity-70 font-medium">{label}</div>
      <div className="text-[20px] font-bold tabular-nums">{value}</div>
    </div>
  );
}
